// components/AgreementTemplateManager.js
// Full template management for owner dashboard.
// Upload, name, version, preview, delete.

import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://brokerconnect.co.uk/api';

const STATUS_STYLE = {
  pending: { label: 'Parsing…',  bg:'#fefce8', color:'#854d0e', border:'#fde047' },
  parsed:  { label: 'Ready',     bg:'#f0fdf4', color:'#166534', border:'#86efac' },
  failed:  { label: 'Failed',    bg:'#fef2f2', color:'#991b1b', border:'#fca5a5' },
};

const FIELD_ICONS = { text:'✏️', email:'📧', tel:'📞', date:'📅', textarea:'📝', checkbox:'☑️', select:'▾' };

export default function AgreementTemplateManager({ token, companyId, companyName, activeTemplateId, onSetLive }) {
  const [templates,    setTemplates]    = useState([]);
  const [uploading,    setUploading]    = useState(false);
  const [uploadError,  setUploadError]  = useState(null);
  const [pollingId,    setPollingId]    = useState(null);
  const [previewTpl,   setPreviewTpl]   = useState(null); // template to preview
  const [editingName,  setEditingName]  = useState(null); // { id, value }
  const [dragOver,     setDragOver]     = useState(false);
  const [displayName,  setDisplayName]  = useState('');
  const [showUpload,   setShowUpload]   = useState(false);
  const fileInput = useRef();
  const pollRef   = useRef();

  const hdrs = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const cq = useCallback(() => `company_id=${encodeURIComponent(companyId || '')}`, [companyId]);

  const loadTemplates = useCallback(async () => {
    if (!companyId) { setTemplates([]); return; }
    try {
      const res  = await fetch(`${API}/agreement-templates/list?${cq()}`, { headers: hdrs() });
      const data = await res.json();
      if (Array.isArray(data)) setTemplates(data);
    } catch (e) { console.error(e); }
  }, [hdrs, cq, companyId]);

  useEffect(() => { loadTemplates(); return () => clearInterval(pollRef.current); }, [loadTemplates]);

  const startPolling = (id) => {
    setPollingId(id);
    pollRef.current = setInterval(async () => {
      try {
        const res  = await fetch(`${API}/agreement-templates/status/${id}?${cq()}`, { headers: hdrs() });
        const data = await res.json();
        if (data.status !== 'pending') {
          clearInterval(pollRef.current);
          setPollingId(null);
          loadTemplates();
        }
      } catch { clearInterval(pollRef.current); setPollingId(null); }
    }, 3000);
  };

  const handleUpload = async (file) => {
    if (!file || !companyId) return;
    setUploadError(null); setUploading(true);
    const form = new FormData();
    form.append('template', file);
    form.append('display_name', displayName || file.name.replace(/\.[^.]+$/, ''));
    try {
      const res  = await fetch(`${API}/agreement-templates/upload?${cq()}`, {
        method: 'POST', headers: hdrs(), body: form
      });
      const data = await res.json();
      if (!res.ok) { setUploadError(data.error || 'Upload failed'); return; }
      setDisplayName('');
      setShowUpload(false);
      await loadTemplates();
      startPolling(data.templateId);
    } catch { setUploadError('Network error — please try again.'); }
    finally { setUploading(false); if (fileInput.current) fileInput.current.value = ''; }
  };

  const saveLabel = async (id, value) => {
    await fetch(`${API}/agreement-templates/${id}/label?${cq()}`, {
      method: 'PATCH',
      headers: { ...hdrs(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ display_name: value })
    });
    setEditingName(null);
    loadTemplates();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    const res  = await fetch(`${API}/agreement-templates/${id}?${cq()}`, { method:'DELETE', headers: hdrs() });
    const data = await res.json();
    if (!res.ok) { alert(data.error || 'Delete failed'); return; }
    loadTemplates();
  };

  const hasTemplates   = templates.length > 0;
  const parsedCount    = templates.filter(t => t.status === 'parsed').length;
  const activeTemplate = templates.find(t => t.id === activeTemplateId) || null;

  // ── Styles ────────────────────────────────────────────────────────────────
  const card = {
    background:'#fff', border:'1px solid #e2e8f0',
    borderRadius:12, padding:24, marginBottom:16,
  };

  if (!companyId) {
    return (
      <div style={{
        background:'#f8fafc', border:'1px dashed #cbd5e0',
        borderRadius:12, padding:32, textAlign:'center', color:'#718096', fontSize:13,
      }}>
        Select a broker company above to manage its introducer agreement templates.
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ margin:'0 0 4px', fontSize:18, fontWeight:700, color:'#0e1b2c' }}>
            Introducer Agreements
          </h2>
          <p style={{ margin:0, fontSize:13, color:'#718096' }}>
            {parsedCount === 0
              ? 'Upload your network\'s introducer agreement to start inviting introducers.'
              : `${parsedCount} agreement${parsedCount > 1 ? 's' : ''} ready · introducers will be asked to complete the form when they sign up`}
          </p>
        </div>
        <button
          onClick={() => setShowUpload(!showUpload)}
          style={{
            padding:'10px 18px', borderRadius:8, border:'none',
            background:'#10b981', color:'#fff', fontWeight:600,
            fontSize:13, cursor:'pointer',
          }}
        >
          + Upload Agreement
        </button>
      </div>

      {/* Live-for-company status (read-only — pushing/taking down happens per-row
          below, once a template is approved, so it's always clear exactly which
          agreement is live) */}
      <div style={{
        display:'flex', alignItems:'center', gap:10,
        padding:'10px 16px', borderRadius:10, marginBottom:20,
        background: activeTemplate ? '#f0fdf4' : '#f8fafc',
        border: `1px solid ${activeTemplate ? '#86efac' : '#e2e8f0'}`,
      }}>
        <span style={{ fontSize:16 }}>{activeTemplate ? '✓' : '—'}</span>
        <p style={{ margin:0, fontSize:13, color: activeTemplate ? '#166534' : '#718096' }}>
          {activeTemplate
            ? <>Live for <strong>{companyName || 'this company'}</strong>: {activeTemplate.display_name}</>
            : <>Not live for <strong>{companyName || 'this company'}</strong> yet — approve a template below, then push it live.</>}
        </p>
      </div>

      {/* No templates — instructions banner */}
      {!hasTemplates && (
        <div style={{
          background:'#fffbeb', border:'1px solid #fcd34d',
          borderRadius:12, padding:20, marginBottom:20,
        }}>
          <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
            <span style={{ fontSize:24, flexShrink:0 }}>⚠️</span>
            <div>
              <p style={{ margin:'0 0 8px', fontWeight:700, color:'#92400e', fontSize:14 }}>
                You need to upload an introducer agreement before you can invite introducers
              </p>
              <p style={{ margin:'0 0 12px', fontSize:13, color:'#78350f', lineHeight:1.6 }}>
                Your introducer agreement is the legal document provided by your network or compliance team
                (e.g. Beneficial, PRIMIS, Mortgage Advice Bureau, Stonebridge). Introducers will complete
                and sign this form when they accept your invite.
              </p>
              <div style={{ fontSize:13, color:'#78350f' }}>
                <p style={{ margin:'0 0 4px', fontWeight:600 }}>How to set up:</p>
                <ol style={{ margin:0, paddingLeft:20, lineHeight:2 }}>
                  <li>Click <strong>Upload Agreement</strong> above</li>
                  <li>Give it a friendly name (e.g. "Beneficial Referral Agreement")</li>
                  <li>Drop in your Word or PDF document</li>
                  <li>Wait ~15 seconds — fields are extracted automatically</li>
                  <li>You're ready to send your first invite</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload panel */}
      {showUpload && (
        <div style={{ ...card, border:'1px solid #10b981' }}>
          <h4 style={{ margin:'0 0 16px', fontSize:14, fontWeight:700, color:'#0e1b2c' }}>
            Upload new agreement
          </h4>

          {/* Display name input */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#4b5a6e', marginBottom:6 }}>
              Agreement name <span style={{ color:'#94a3b8', fontWeight:400 }}>(shown in invite picker)</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="e.g. Beneficial Referral Agreement, PRIMIS Introduction Form…"
              style={{
                width:'100%', boxSizing:'border-box',
                padding:'10px 12px', borderRadius:8,
                border:'1px solid #e2e8f0', fontSize:13,
                color:'#2d3748', outline:'none',
              }}
            />
          </div>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files[0]); }}
            onClick={() => !uploading && fileInput.current.click()}
            style={{
              border: `2px dashed ${dragOver ? '#10b981' : '#cbd5e0'}`,
              borderRadius:10, padding:32, textAlign:'center',
              cursor: uploading ? 'not-allowed' : 'pointer',
              background: dragOver ? '#f0fdf4' : '#f7fafc',
              transition:'all 0.2s',
              opacity: uploading ? 0.6 : 1,
            }}
          >
            <input
              ref={fileInput} type="file" accept=".pdf,.doc,.docx"
              style={{ display:'none' }}
              onChange={e => handleUpload(e.target.files[0])}
              disabled={uploading}
            />
            <div style={{ fontSize:36, marginBottom:8 }}>{uploading ? '⏳' : '📄'}</div>
            <p style={{ margin:'0 0 4px', fontWeight:600, color:'#4b5a6e' }}>
              {uploading ? 'Uploading and parsing…' : 'Drop your agreement here, or click to browse'}
            </p>
            <p style={{ margin:0, fontSize:12, color:'#a0aec0' }}>PDF or Word (.docx) — max 20 MB</p>
          </div>

          {uploadError && (
            <div style={{ marginTop:12, padding:'10px 14px', borderRadius:8,
              background:'#fef2f2', border:'1px solid #fca5a5', fontSize:13, color:'#991b1b' }}>
              ⚠️ {uploadError}
            </div>
          )}

          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}>
            <button onClick={() => { setShowUpload(false); setUploadError(null); setDisplayName(''); }}
              style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #e2e8f0',
                background:'#fff', color:'#4b5a6e', fontSize:13, cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Template list */}
      {templates.map(t => (
        <TemplateCard
          key={t.id}
          template={t}
          isPolling={pollingId === t.id}
          editingName={editingName?.id === t.id ? editingName.value : null}
          onEditName={(val) => setEditingName({ id: t.id, value: val })}
          onSaveName={(val) => saveLabel(t.id, val)}
          onCancelEdit={() => setEditingName(null)}
          onPreview={() => setPreviewTpl(t)}
          onDelete={() => remove(t.id)}
          onFieldsSaved={() => loadTemplates()}
          token={token}
          companyId={companyId}
          isActive={t.id === activeTemplateId}
          onLiveChanged={onSetLive}
        />
      ))}

      {/* Preview modal */}
      {previewTpl && (
        <PreviewModal
          template={previewTpl}
          onClose={() => setPreviewTpl(null)}
        />
      )}
    </div>
  );
}

// ── Form-filling (mapping) controls ──────────────────────────────────────────
// Auto-mapping anchors each field to its exact spot on the form; the broker
// previews a sample-filled PDF and approves before real signings fill it.
function FormFillControls({ t, token, onChanged, companyId, isActive, onLiveChanged }) {
  const [busy, setBusy] = useState(null);
  const hdrs = { Authorization: `Bearer ${token}` };
  const status = t.mapping_status; // undefined/null | 'draft' | 'approved'

  const call = async (kind, path, opts = {}) => {
    setBusy(kind);
    try {
      const sep = path.includes('?') ? '&' : '?';
      const res = await fetch(`${API}/agreement-templates/${t.id}/${path}${sep}company_id=${encodeURIComponent(companyId || '')}`, { headers: hdrs, ...opts });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Request failed');
      }
      return res;
    } finally { setBusy(null); }
  };

  const runMap = async () => {
    try { await call('map', 'map', { method: 'POST' }); onChanged(); }
    catch (e) { alert(e.message); }
  };
  const previewFilled = async () => {
    try {
      const res = await call('preview', 'preview-filled');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { alert(e.message); }
  };
  const approve = async () => {
    if (!window.confirm('Approve this layout? New signed agreements will be filled in at these positions.')) return;
    try { await call('approve', 'mapping/approve', { method: 'POST' }); onChanged(); }
    catch (e) { alert(e.message); }
  };

  const toggleLive = async (live) => {
    const msg = live
      ? `Push "${t.display_name}" live? This replaces any agreement currently live for this company.`
      : `Take down "${t.display_name}"? Brokers will go back to the simple invite-without-agreement flow.`;
    if (!window.confirm(msg)) return;
    setBusy(live ? 'push' : 'takedown');
    try {
      const res = await fetch(`${API}/agreement-templates/${t.id}/live?company_id=${encodeURIComponent(companyId || '')}`, {
        method: 'PATCH',
        headers: { ...hdrs, 'Content-Type': 'application/json' },
        body: JSON.stringify({ live }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || 'Request failed'); }
      onChanged();
      if (onLiveChanged) onLiveChanged();
    } catch (e) { alert(e.message); }
    finally { setBusy(null); }
  };

  const chip = status === 'approved'
    ? { label: `Form filling active ✓ (${t.mapped_field_count} fields)`, bg:'#f0fdf4', color:'#166534', border:'#86efac' }
    : status === 'draft'
    ? { label: `Form filling: review needed (${t.mapped_field_count} fields placed)`, bg:'#fffbeb', color:'#92400e', border:'#fcd34d' }
    : { label: 'Form filling: not set up — signed PDFs attach the blank form + answers page', bg:'#f8fafc', color:'#64748b', border:'#e2e8f0' };

  const btn = (bg, color, border) => ({
    padding:'5px 11px', borderRadius:8, border:`1px solid ${border}`,
    background:bg, color, fontSize:11.5, fontWeight:600, cursor:'pointer',
  });

  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:10, flexWrap:'wrap' }}>
      <span style={{ fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20,
        background:chip.bg, color:chip.color, border:`1px solid ${chip.border}` }}>
        {chip.label}
      </span>
      {!status && (
        <button onClick={runMap} disabled={!!busy} style={btn('#eff6ff','#1d4ed8','#93c5fd')}>
          {busy === 'map' ? 'Mapping…' : '⚙️ Set up form filling'}
        </button>
      )}
      {status && (
        <>
          <button onClick={previewFilled} disabled={!!busy} style={btn('#eff6ff','#1d4ed8','#93c5fd')}>
            {busy === 'preview' ? 'Generating…' : '👁 Preview filled sample'}
          </button>
          {status === 'draft' && (
            <button onClick={approve} disabled={!!busy} style={btn('#f0fdf4','#166534','#86efac')}>
              {busy === 'approve' ? 'Approving…' : '✓ Approve layout'}
            </button>
          )}
          <button onClick={runMap} disabled={!!busy} style={btn('#f8fafc','#64748b','#e2e8f0')}>
            {busy === 'map' ? 'Mapping…' : '↻ Re-map'}
          </button>
          {status === 'approved' && (
            isActive ? (
              <button onClick={() => toggleLive(false)} disabled={!!busy} style={btn('#fff','#991b1b','#fca5a5')}>
                {busy === 'takedown' ? 'Taking down…' : '✓ Live — take down'}
              </button>
            ) : (
              <button onClick={() => toggleLive(true)} disabled={!!busy} style={btn('#10b981','#fff','#10b981')}>
                {busy === 'push' ? 'Pushing…' : 'Push to broker company'}
              </button>
            )
          )}
        </>
      )}
    </div>
  );
}

// ── Template card ─────────────────────────────────────────────────────────────
function TemplateCard({ template: t, isPolling, editingName, onEditName, onSaveName, onCancelEdit, onPreview, onDelete, onFieldsSaved, token, companyId, isActive, onLiveChanged }) {
  const [expanded, setExpanded] = useState(false);
  const s = STATUS_STYLE[t.status] || STATUS_STYLE.pending;
  const fieldCount = t.extracted_fields?.length ?? 0;

  // This is the template actually live for the company, but its mapping isn't
  // approved right now (e.g. someone hit Re-map or edited fields and hasn't
  // re-approved yet) -- real introducers signing at this exact moment get a
  // blank-form + certificate-only PDF, silently. Flag it hard (Tim, 9 July).
  const liveUnapproved = isActive && t.mapping_status !== 'approved';
  const liveApproved    = isActive && t.mapping_status === 'approved';

  return (
    <div style={{
      background: liveUnapproved ? '#fef2f2' : liveApproved ? '#f0fdf4' : '#fff',
      border: liveUnapproved ? '2px solid #dc2626' : liveApproved ? '2px solid #10b981' : '1px solid #e2e8f0',
      borderRadius:12, padding:20, marginBottom:12,
    }}>
      {liveUnapproved && (
        <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:8,
          background:'#fee2e2', border:'1px solid #fca5a5', color:'#991b1b',
          fontSize:12.5, fontWeight:600 }}>
          ⚠️ This is the live template but the layout isn't approved — any introducer
          signing right now gets a blank-form + certificate-only PDF. Preview, then
          Approve layout below.
        </div>
      )}
      {/* Top row */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
        <div style={{ flex:1, minWidth:0 }}>
          {/* Name — editable */}
          {editingName !== null ? (
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <input
                autoFocus
                value={editingName}
                onChange={e => onEditName(e.target.value)}
                onKeyDown={e => { if (e.key==='Enter') onSaveName(editingName); if (e.key==='Escape') onCancelEdit(); }}
                style={{ flex:1, padding:'6px 10px', borderRadius:6, border:'1px solid #10b981',
                  fontSize:14, fontWeight:600, color:'#0e1b2c', outline:'none' }}
              />
              <button onClick={() => onSaveName(editingName)}
                style={{ padding:'6px 12px', borderRadius:6, border:'none',
                  background:'#10b981', color:'#fff', fontSize:12, cursor:'pointer' }}>
                Save
              </button>
              <button onClick={onCancelEdit}
                style={{ padding:'6px 12px', borderRadius:6, border:'1px solid #e2e8f0',
                  background:'#fff', color:'#4b5a6e', fontSize:12, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <p style={{ margin:0, fontSize:15, fontWeight:700, color:'#0e1b2c', truncate:true }}>
                {t.display_name || t.original_name}
              </p>
              <button onClick={() => onEditName(t.display_name || t.original_name)}
                style={{ background:'none', border:'none', fontSize:12, color:'#a0aec0',
                  cursor:'pointer', padding:'0 4px' }} title="Rename">
                ✏️
              </button>
              {t.submitted_by_broker && t.mapping_status !== 'approved' && (
                <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20,
                  background:'#eff6ff', color:'#1d4ed8', border:'1px solid #93c5fd' }}>
                  Submitted by broker — needs review
                </span>
              )}
            </div>
          )}

          {/* Meta row */}
          <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:6, flexWrap:'wrap' }}>
            {/* Status badge */}
            <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20,
              background:s.bg, color:s.color, border:`1px solid ${s.border}` }}>
              {isPolling ? 'Parsing…' : s.label}
            </span>
            {/* Version badge */}
            <span style={{ fontSize:11, color:'#94a3b8', fontWeight:500 }}>
              v{t.version}
            </span>
            {isActive && (
              <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20,
                background:'#f0fdf4', color:'#166534', border:'1px solid #86efac' }}>
                ✓ Live
              </span>
            )}
            {/* Field count */}
            {t.status === 'parsed' && (
              <span style={{ fontSize:11, color:'#718096' }}>
                {fieldCount} field{fieldCount !== 1 ? 's' : ''}
              </span>
            )}
            {/* Original filename */}
            <span style={{ fontSize:11, color:'#cbd5e0' }}>
              {t.original_name}
            </span>
          </div>

          {t.status === 'failed' && (
            <p style={{ margin:'8px 0 0', fontSize:12, color:'#dc2626' }}>
              Parse error: {t.parse_error}
            </p>
          )}

          {t.status === 'parsed' && (
            <FormFillControls t={t} token={token} onChanged={onFieldsSaved} companyId={companyId}
              isActive={isActive} onLiveChanged={onLiveChanged} />
          )}
        </div>

        {/* Actions */}
        <div style={{ display:'flex', gap:8, flexShrink:0 }}>
          {t.status === 'parsed' && (
            <>
              <button onClick={() => setExpanded(!expanded)}
                style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #e2e8f0',
                  background:'#f7fafc', color:'#4b5a6e', fontSize:12, cursor:'pointer' }}>
                {expanded ? 'Hide fields' : 'Fields'}
              </button>
              <button onClick={onPreview}
                style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #10b981',
                  background:'#f0fdf4', color:'#166534', fontSize:12, cursor:'pointer',
                  fontWeight:600 }}>
                👁 Preview
              </button>
            </>
          )}
          <button onClick={onDelete}
            style={{ padding:'6px 12px', borderRadius:8, border:'1px solid #fca5a5',
              background:'#fff', color:'#dc2626', fontSize:12, cursor:'pointer' }}>
            Delete
          </button>
        </div>
      </div>

      {/* Editable field list */}
      {expanded && t.extracted_fields && (
        <FieldEditor
          templateId={t.id}
          initialFields={t.extracted_fields}
          token={token}
          onSaved={onFieldsSaved}
          companyId={companyId}
        />
      )}
    </div>
  );
}

// ── Editable field editor ─────────────────────────────────────────────────────
const FIELD_TYPES = ['text','email','tel','date','textarea','checkbox','select'];

function FieldEditor({ templateId, initialFields, token, onSaved, companyId }) {
  const [fields,  setFields]  = useState(() => initialFields.map(f => ({ ...f })));
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');

  const inp = (extra = {}) => ({
    padding:'5px 8px', borderRadius:6, border:'1px solid #e2e8f0',
    fontSize:12, color:'#374151', background:'#fff', ...extra,
  });

  const updateField = (idx, key, val) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f));
    setSaved(false);
  };

  const removeField = (idx) => setFields(prev => prev.filter((_, i) => i !== idx));

  const addField = () => setFields(prev => [...prev, {
    key: `field_${Date.now()}`, label: 'New Field', type: 'text',
    required: false, placeholder: '', section: prev[0]?.section || 'Your Details', options: [],
  }]);

  const saveFields = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/agreement-templates/${templateId}/fields?company_id=${encodeURIComponent(companyId || '')}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields }),
      });
      const d = await res.json();
      if (!res.ok) { setError(d.error || 'Save failed'); return; }
      setSaved(true);
      onSaved();
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid #e2e8f0' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <p style={{ margin:0, fontSize:12, fontWeight:700, color:'#64748b',
          textTransform:'uppercase', letterSpacing:'0.05em' }}>
          Form Fields ({fields.length})
        </p>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {saved && <span style={{ fontSize:11, color:'#10b981' }}>✓ Saved</span>}
          {error && <span style={{ fontSize:11, color:'#dc2626' }}>{error}</span>}
          <button onClick={addField}
            style={{ padding:'5px 12px', borderRadius:6, border:'1px dashed #d1d5db',
              background:'#f9fafb', color:'#4b5a6e', fontSize:12, cursor:'pointer' }}>
            + Add field
          </button>
          <button onClick={saveFields} disabled={saving}
            style={{ padding:'5px 14px', borderRadius:6, border:'none',
              background:'#10b981', color:'#fff', fontSize:12, fontWeight:600,
              cursor:'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
        {/* Header row */}
        <div style={{ display:'grid', gridTemplateColumns:'2fr 100px 80px 80px 28px',
          gap:6, padding:'0 4px', fontSize:10, fontWeight:700,
          color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em' }}>
          <span>Label</span><span>Type</span><span>Section</span>
          <span style={{ textAlign:'center' }}>Required</span><span/>
        </div>

        {fields.map((f, idx) => (
          <div key={f.key} style={{
            display:'grid', gridTemplateColumns:'2fr 100px 80px 80px 28px',
            gap:6, alignItems:'center', padding:'6px 8px', borderRadius:8,
            background: idx % 2 === 0 ? '#f9fafb' : '#fff',
            border:'1px solid #f0f0f0',
          }}>
            {/* Label */}
            <input
              value={f.label}
              onChange={e => updateField(idx, 'label', e.target.value)}
              style={{ ...inp(), width:'100%', boxSizing:'border-box' }}
            />
            {/* Type */}
            <select value={f.type} onChange={e => updateField(idx, 'type', e.target.value)} style={inp()}>
              {FIELD_TYPES.map(t => (
                <option key={t} value={t}>{FIELD_ICONS[t] || '✏️'} {t}</option>
              ))}
            </select>
            {/* Section */}
            <input
              value={f.section || ''}
              onChange={e => updateField(idx, 'section', e.target.value)}
              placeholder="Section"
              style={{ ...inp(), width:'100%', boxSizing:'border-box' }}
            />
            {/* Required toggle */}
            <div style={{ display:'flex', justifyContent:'center' }}>
              <button
                type="button"
                onClick={() => updateField(idx, 'required', !f.required)}
                style={{
                  width:36, height:20, borderRadius:10, border:'none',
                  background: f.required ? '#10b981' : '#d1d5db',
                  cursor:'pointer', position:'relative', transition:'background 0.2s',
                }}
              >
                <span style={{
                  position:'absolute', top:2, left: f.required ? 18 : 2,
                  width:16, height:16, borderRadius:'50%', background:'#fff',
                  transition:'left 0.2s', boxShadow:'0 1px 2px rgba(0,0,0,0.2)',
                }} />
              </button>
            </div>
            {/* Remove */}
            <button onClick={() => removeField(idx)}
              style={{ background:'none', border:'none', color:'#e11d48',
                fontSize:14, cursor:'pointer', padding:0, lineHeight:1 }}>
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Preview modal — shows form exactly as introducer sees it ──────────────────
function PreviewModal({ template, onClose }) {
  const fields = template.extracted_fields || [];
  const sections = fields.reduce((acc, f) => {
    const s = f.section || 'Your Details';
    if (!acc[s]) acc[s] = [];
    acc[s].push(f);
    return acc;
  }, {});

  const inp = (extra = {}) => ({
    width:'100%', boxSizing:'border-box',
    padding:'8px 10px', borderRadius:6,
    border:'1px solid #e2e8f0', fontSize:13,
    color:'#2d3748', background:'#fff',
    ...extra,
  });

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
      display:'flex', alignItems:'center', justifyContent:'center',
      zIndex:1000, padding:20,
    }}>
      <div style={{
        background:'#fff', borderRadius:16, width:'100%', maxWidth:580,
        maxHeight:'85vh', overflow:'hidden', display:'flex', flexDirection:'column',
      }}>
        {/* Modal header */}
        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #f0f0f0',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h3 style={{ margin:'0 0 2px', fontSize:16, fontWeight:700, color:'#0e1b2c' }}>
              Preview — {template.display_name || template.original_name}
            </h3>
            <p style={{ margin:0, fontSize:12, color:'#94a3b8' }}>
              This is exactly what introducers will see · v{template.version} · {fields.length} fields
            </p>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', fontSize:22,
              color:'#94a3b8', cursor:'pointer', padding:4 }}>×</button>
        </div>

        {/* Scrollable form */}
        <div style={{ overflowY:'auto', padding:'20px 24px', flex:1 }}>
          {Object.entries(sections).map(([section, sFields]) => (
            <div key={section} style={{ marginBottom:24 }}>
              <p style={{ margin:'0 0 12px', fontSize:11, fontWeight:700,
                textTransform:'uppercase', letterSpacing:'0.06em', color:'#94a3b8',
                paddingBottom:6, borderBottom:'1px solid #f0f0f0' }}>
                {section}
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {sFields.map(f => (
                  <div key={f.key}>
                    <label style={{ display:'block', fontSize:12, fontWeight:600,
                      color:'#4b5a6e', marginBottom:4 }}>
                      {f.label}
                      {f.required && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}
                    </label>
                    {(() => {
                      const isYesNo = f.type === 'select' &&
                        (f.options||[]).map(o=>o.toLowerCase()).sort().join(',') === 'no,yes';
                      const isToggle = f.type === 'checkbox' || isYesNo;
                      if (isToggle) return (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                          padding:'10px 14px', borderRadius:10, border:'1px solid #e2e8f0',
                          background:'#f9fafb', opacity:0.7 }}>
                          <span style={{ fontSize:13, color:'#374151' }}>{f.label}</span>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div style={{ width:44, height:24, borderRadius:12,
                              background:'#d1d5db', position:'relative' }}>
                              <div style={{ position:'absolute', top:3, left:3, width:18,
                                height:18, borderRadius:'50%', background:'#fff',
                                boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                            </div>
                            <span style={{ fontSize:12, color:'#9ca3af', fontWeight:600 }}>No</span>
                          </div>
                        </div>
                      );
                      if (f.type === 'textarea') return (
                        <textarea rows={3} placeholder={f.placeholder} disabled
                          style={{ ...inp(), resize:'vertical', opacity:0.7 }} />
                      );
                      if (f.type === 'select') return (
                        <select disabled style={{ ...inp(), opacity:0.7 }}>
                          <option>Select…</option>
                          {(f.options||[]).map(o => <option key={o}>{o}</option>)}
                        </select>
                      );
                      return (
                        <input type={f.type || 'text'} placeholder={f.placeholder} disabled
                          style={{ ...inp(), opacity:0.7 }} />
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div style={{ background:'#f7fafc', border:'1px solid #e2e8f0',
            borderRadius:8, padding:12, fontSize:12, color:'#718096', marginTop:8 }}>
            ℹ️ This is a read-only preview. Introducers will complete and submit this form before signing.
          </div>
        </div>
      </div>
    </div>
  );
}
